import { Environment } from 'marketing-site/lib/environment'
import { IntercomContext } from 'marketing-site/src/components/context/IntercomContext'
import { useServerPropsContext } from 'marketing-site/src/components/context/ServerPropsContext'
import React, { useContext, useEffect } from 'react'
import { IAssignedVariations, useAssignedVariations } from '../context/AssignedVariationsContext'
import useLocaleCode from 'marketing-site/src/library/hooks/useLocaleCode'
import { useSessionStorage } from 'marketing-site/src/library/hooks/use-session-storage'
import { incrementMetric } from 'marketing-site/lib/frontendMetrics'

const INTERCOM_APP_ID = 'tx2p130c'
const TEAM_WEB_APP_ID = 'pi9zubh0'

const AD_ATTRIBUTES = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'keyword',
  'ad_collection',
  'ad',
  'ad_collection_identifier',
  'ad_identifier',
  'variation',
  'agn',
  'kw',
  'loc',
  'adpos',
  'creative',
  'matchtype',
  'utm_network',
  'type',
]

type Settings = Record<string, string | string[] | boolean | undefined>
type Query = Settings | undefined

function buildIntercomSettings(
  appId: string,
  query: Query = {},
  assignedVariations: IAssignedVariations,
  hideLauncher = false,
  localeCode = 'en',
): Settings {
  const settings: Settings = {
    app_id: appId, // eslint-disable-line @typescript-eslint/naming-convention
  }

  if (localeCode) {
    settings.language_override = localeCode === 'en-US' ? 'en' : localeCode // eslint-disable-line @typescript-eslint/naming-convention
  }

  if (hideLauncher) {
    settings.hide_default_launcher = true // eslint-disable-line @typescript-eslint/naming-convention
  }

  // Add any ad attributes to intercom settings
  Object.keys(query).reduce((result: Settings, key) => {
    if (AD_ATTRIBUTES.includes(key)) {
      const escapedQueryParam = (query[key] as string).replace(/<\/?[^>]+(>|$)/g, '')
      result[key] = escapedQueryParam
    }
    return result
  }, settings)

  settings['sees_vbp_feb20'] = false
  settings['sees_vbp2'] = true
  settings['pricing_model_shown'] = 'VBP 2.0'

  Object.entries(assignedVariations).forEach(([experimentKey, { variationKey }]) => {
    if (variationKey) {
      settings[`experiment-${experimentKey}`] = variationKey
    }
  })

  return settings
}

function getAppIdFromEnvironment(environment: Environment) {
  return environment === Environment.Production ? INTERCOM_APP_ID : TEAM_WEB_APP_ID
}

interface IBodyPartProps {
  disableMessenger?: boolean
  hideLauncher?: boolean
  nonce?: string
}

export const BodyPart: React.FC<IBodyPartProps> = function ({
  disableMessenger,
  hideLauncher,
  nonce,
}: IBodyPartProps) {
  const assignedVariations = useAssignedVariations()
  const { environment, query } = useServerPropsContext()
  const appId = getAppIdFromEnvironment(environment)
  const { setIsMessengerOpen } = useContext(IntercomContext)
  const localeCode = useLocaleCode()
  const [, setRecordedGtmId] = useSessionStorage<boolean>('recordedGtmId', false)

  useEffect(() => {
    fetch('https://widget.intercom.io/widget/tx2p130c')
      .then(() =>
        incrementMetric('GtmJS.MarketingSite.intercomWidgetAdblockCheck', { blocked: 'false' }),
      )
      .catch(() =>
        incrementMetric('GtmJS.MarketingSite.intercomWidgetAdblockCheck', { blocked: 'true' }),
      )
  }, [])

  useEffect(() => {
    if (!disableMessenger) {
      const intercomSettings = buildIntercomSettings(
        appId,
        query,
        assignedVariations,
        hideLauncher,
        localeCode,
      )
      window.Intercom('onShow', () => setIsMessengerOpen(true))

      if (typeof intercomSettings['gtm_cookie_identifier'] === 'string') {
        setRecordedGtmId(true)
      }
    }
  }, [disableMessenger, appId, assignedVariations, query, hideLauncher, localeCode])

  if (disableMessenger) {
    return (
      <script
        type="text/javascript"
        nonce={nonce}
        dangerouslySetInnerHTML={{
          __html: `window.Intercom('hide');`,
        }}
      />
    )
  }

  return (
    <script
      type="text/javascript"
      nonce={nonce}
      dangerouslySetInnerHTML={{
        __html: `let settings = ${JSON.stringify(
          buildIntercomSettings(appId, query, assignedVariations, hideLauncher, localeCode),
        )};
        let gtmCookieIdentifier = undefined;
        document.cookie.split('; ').forEach((c) => {if (c.startsWith('gtm_id=')) gtmCookieIdentifier = c.split('gtm_id=')[1]});
        if (gtmCookieIdentifier) {
          settings['gtm_cookie_identifier'] = gtmCookieIdentifier
        };
        window.Intercom('boot', settings);`,
      }}
    />
  )
}

interface IHeadPartProps {
  nonce?: string
  environment: Environment
}

export const HeadPart = ({ nonce, environment }: IHeadPartProps) => {
  const appId = getAppIdFromEnvironment(environment)

  return (
    <script
      nonce={nonce}
      type="text/javascript"
      dangerouslySetInnerHTML={{
        __html: `(function(){ if (window.Intercom) { return; }var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',intercomSettings);}else{var d=document;var i=function(){i.c(arguments)};i.q=[];i.c=function(args){i.q.push(args)};w.Intercom=i;function l(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/${appId}';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);}if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})()`,
      }}
    />
  )
}
